import { Fragment, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { AnnouncementDialog } from '@/components/AnnouncementDialog';
import { localize, useAnnouncementFeed, type FeedItem } from '@/components/announcementFeed';
import { useAuth } from '@/stores/auth';
import { usePreferences } from '@/stores/preferences';
import { useShellNav } from '@/stores/shellNav';

/** Pixels of scroll per second — constant, so duration naturally scales with
 *  content length rather than every headline taking the same time. */
const SCROLL_SPEED_PX_S = 60;

/**
 * The dashboard's announcement ticker.
 *
 * Renders nothing when there is nothing to show. Human-authored only —
 * system alerts (low stock, pending approvals) live in the separate Action
 * Needed card, never here.
 */
export function AnnouncementTicker() {
  const can = useAuth((s) => s.can);
  const navigate = useShellNav((s) => s.navigate);
  const reducedMotion = usePreferences((s) => s.prefs.reducedMotion);
  const language = usePreferences((s) => s.prefs.language);

  const [paused, setPaused] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [openItem, setOpenItem] = useState<FeedItem | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const seenUrgentIds = useRef<Set<string>>(new Set());

  const feed = useAnnouncementFeed();

  const items = feed.data ?? [];
  // The open notice as it is now; undefined once it has left the feed.
  const openNow = openItem ? items.find((i) => i.id === openItem.id) : undefined;
  // Local page cursor clamped to the current data, rather than synced via an
  // effect: dismissing the last item on a page should not need a render pass
  // that briefly points past the end of the array.
  const pageIndexClamped = items.length === 0 ? 0 : Math.min(pageIndex, items.length - 1);

  // Announce a newly-arrived urgent notice once, politely — never on every
  // poll, and never for anything already seen.
  useEffect(() => {
    const urgentNow = (feed.data ?? []).filter((i) => i.priority === 'URGENT');
    const fresh = urgentNow.filter((i) => !seenUrgentIds.current.has(i.id));
    for (const i of urgentNow) seenUrgentIds.current.add(i.id);
    if (fresh.length > 0) {
      setAnnouncement(
        fresh.length === 1
          ? `Urgent: ${fresh[0]!.headlineEn}`
          : `${fresh.length} new urgent announcements`,
      );
    }
  }, [feed.data]);

  if (items.length === 0) {
    // The last notice can end while someone is reading it; keep it open.
    return openItem ? (
      <AnnouncementDialog item={openItem} live={false} onClose={() => setOpenItem(null)} />
    ) : null;
  }

  return (
    <section className="ticker" aria-label="Announcements">
      <span className="ticker__label" aria-hidden="true">
        Updates
      </span>

      {reducedMotion ? (
        <StaticTicker
          items={items}
          index={pageIndexClamped}
          onIndex={setPageIndex}
          language={language}
          onOpen={setOpenItem}
        />
      ) : (
        <ScrollingTicker items={items} paused={paused} language={language} onOpen={setOpenItem} />
      )}

      <div className="ticker__controls">
        {!reducedMotion && (
          <button
            className="ticker__pause"
            onClick={() => setPaused((v) => !v)}
            aria-pressed={paused}
          >
            {paused ? '▶ Play' : '⏸ Pause'}
          </button>
        )}
        {can('com.announcement.read') && (
          <button className="ticker__manage" onClick={() => navigate('home-announce')}>
            Manage
          </button>
        )}
      </div>

      {/* Screen-reader fallback: every headline, always present, regardless
          of which visual mode is active. */}
      <ul className="sr-only">
        {items.map((i) => (
          <li key={i.id}>{localize(i, language).headline}</li>
        ))}
      </ul>

      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {openItem && (
        <AnnouncementDialog
          item={openNow ?? openItem}
          live={openNow !== undefined}
          onClose={() => setOpenItem(null)}
        />
      )}
    </section>
  );
}

function ScrollingTicker({
  items,
  paused,
  language,
  onOpen,
}: {
  items: FeedItem[];
  paused: boolean;
  language: string;
  onOpen: (item: FeedItem) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [duration, setDuration] = useState(20);

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () => setDuration(Math.max(el.scrollWidth / SCROLL_SPEED_PX_S, 8));
    measure();
    // Guarded rather than assumed: older browsers and the jsdom test
    // environment do not implement ResizeObserver, and a one-off measurement
    // at mount is still correct, just not resize-reactive.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [items]);

  return (
    <div
      className="ticker__viewport"
      aria-hidden="true"
      style={{ '--ticker-duration': `${duration}s` } as CSSProperties}
    >
      <div className={`ticker__track ${paused ? 'ticker__track--paused' : ''}`} ref={trackRef}>
        {[0, 1].map((copy) => (
          <div className="ticker__set" key={copy}>
            {items.map((item, i) => (
              <Fragment key={`${copy}-${item.id}`}>
                <TickerChip item={item} language={language} onOpen={onOpen} />
                {i < items.length - 1 && (
                  <span className="ticker__sep" aria-hidden="true">
                    •
                  </span>
                )}
              </Fragment>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function StaticTicker({
  items,
  index,
  onIndex,
  language,
  onOpen,
}: {
  items: FeedItem[];
  index: number;
  onIndex: (i: number) => void;
  language: string;
  onOpen: (item: FeedItem) => void;
}) {
  const item = items[index];
  if (!item) return null;

  return (
    <div className="ticker__static">
      <button
        className="ticker__nav"
        onClick={() => onIndex((index - 1 + items.length) % items.length)}
        aria-label="Previous announcement"
      >
        ‹
      </button>
      <TickerChip item={item} language={language} onOpen={onOpen} focusable />
      <button
        className="ticker__nav"
        onClick={() => onIndex((index + 1) % items.length)}
        aria-label="Next announcement"
      >
        ›
      </button>
      <span className="ticker__count">
        {index + 1} of {items.length}
      </span>
    </div>
  );
}

/** A small framed-landscape mark: "this one has a picture, click to see it". */
function PictureGlyph() {
  return (
    <svg
      className="ticker__picture"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="5.5" cy="6.5" r="1.3" fill="currentColor" />
      <path d="M2.5 12.5 6.5 8.5l2.5 2.5 2-2 3 3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

/** One headline as the ticker shows it. Exported for the composer's preview. */
export function TickerChip({
  item,
  language,
  onOpen,
  focusable,
}: {
  item: FeedItem;
  language: string;
  onOpen: (item: FeedItem) => void;
  focusable?: boolean;
}) {
  const { headline } = localize(item, language);

  return (
    <button
      className={`ticker__chip ticker__chip--${item.priority.toLowerCase()}`}
      onClick={() => onOpen(item)}
      tabIndex={focusable ? 0 : -1}
    >
      {item.priority === 'URGENT' && (
        <>
          <span aria-hidden="true">⚠ </span>
          <span className="ticker__badge">Urgent</span>{' '}
        </>
      )}
      {item.priority === 'IMPORTANT' && (
        <span className="ticker__badge ticker__badge--warn">Important </span>
      )}
      {headline}
      {item.imageKey && (
        <>
          {' '}
          <PictureGlyph />
          <span className="sr-only"> (has a picture)</span>
        </>
      )}
    </button>
  );
}
